// Readable labels for a listing's public attributes (brand, gender, category, size, condition),
// shared by the listing page head (title, description, Product schema) and the visible details
// (BSC-4). Each helper returns null when the listing has no usable value.
import { filters, sizes } from '../config/marketplace-custom-config';
import { findOptionsForSelectFilter } from './search';
import { SIZE_TYPE, VIDEO_TYPE } from './types';

// Categories that say nothing about the item, so titles and descriptions leave them out
const UNDESCRIPTIVE_CATEGORIES = ['other', 'all'];

const CONDITION_LABELS = {
  used: 'Used',
  'new-with-tag': 'New with tags',
  'new-without-tag': 'New without tags',
};

const SCHEMA_CONDITIONS = {
  used: 'https://schema.org/UsedCondition',
  'new-with-tag': 'https://schema.org/NewCondition',
  'new-without-tag': 'https://schema.org/NewCondition',
};

// Older listings store condition as typed or as the filter label: "used ", "nwt",
// "nwt(new with tag)", "nwot(new without tag)". Each maps to its option key.
const LEGACY_CONDITIONS = {
  nwt: 'new-with-tag',
  nwot: 'new-without-tag',
};

const clean = value => (typeof value === 'string' && value.trim() ? value.trim() : null);

// The condition option key, or null when the listing has none or an unknown value
const conditionKey = publicData => {
  const condition = clean(publicData?.condition)?.toLowerCase();
  if (!condition) {
    return null;
  }
  if (CONDITION_LABELS[condition]) {
    return condition;
  }
  const abbreviation = condition.replace(/\s*\(.*$/, '');
  return LEGACY_CONDITIONS[abbreviation] || null;
};

const optionLabel = (filterId, key) => {
  const option = findOptionsForSelectFilter(filterId, filters).find(o => o.key === key);
  return option ? clean(option.label) : null;
};

/**
 * Brand as sellers typed or picked it. Older listings may hold a filter key (air_jordan), which
 * is mapped to its label.
 */
export const brandLabel = publicData => {
  const brand = clean(publicData?.brand);
  return brand ? optionLabel('brand', brand) || brand : null;
};

export const genderLabel = publicData => optionLabel('gender', publicData?.gender);

/**
 * Product-type label (Tops, Jeans, Sneakers). Other and All are treated as missing.
 */
export const categoryLabel = publicData => {
  const category = clean(publicData?.category);
  if (!category || UNDESCRIPTIVE_CATEGORIES.includes(category.toLowerCase())) {
    return null;
  }
  const label = optionLabel('category', category);
  return label && !UNDESCRIPTIVE_CATEGORIES.includes(label.toLowerCase()) ? label : null;
};

/**
 * Size label, or the seller's own text when they chose a custom size.
 */
export const sizeLabel = publicData => {
  const size = clean(publicData?.size);
  if (!size) {
    return null;
  }
  if (size === SIZE_TYPE) {
    return clean(publicData?.customSize);
  }
  const option = sizes.find(s => s.key === size);
  return (option && clean(option.label)) || size;
};

/**
 * "size M", or "One Size" on its own so it never reads "size One Size".
 */
export const sizePhrase = publicData => {
  const size = sizeLabel(publicData);
  if (!size) {
    return null;
  }
  return /^one size$/i.test(size) ? 'One Size' : `size ${size}`;
};

/**
 * Used, New with tags or New without tags; null for listings created before condition existed.
 */
export const conditionLabel = publicData => CONDITION_LABELS[conditionKey(publicData)] || null;

/**
 * Product schema itemCondition: both new conditions publish as new, Used as used.
 */
export const schemaItemCondition = publicData =>
  SCHEMA_CONDITIONS[conditionKey(publicData)] || null;

const CLOUDINARY_UPLOAD = /^(?:https?:)?\/\/res\.cloudinary\.com\/([^/]+)\/image\/upload\//;

/**
 * Cloudinary photo URL over https, optionally capped to a width with automatic quality. Other
 * URLs are returned unchanged.
 */
export const listingPhotoUrl = (url, width) => {
  if (typeof url !== 'string' || !CLOUDINARY_UPLOAD.test(url)) {
    return url;
  }
  const transformation = width ? `c_limit,w_${width},q_auto/` : '';
  return url.replace(
    CLOUDINARY_UPLOAD,
    (match, cloudName) => `https://res.cloudinary.com/${cloudName}/image/upload/${transformation}`
  );
};

/**
 * Every still photo the seller uploaded (webp and GIF, the tag photo included), in upload
 * order. Videos are left out: search engines' image fields do not take them.
 */
export const listingStillImageUrls = publicData =>
  (publicData?.listingVideoAndImagesUrl || [])
    .filter(media => media?.url && media.type !== VIDEO_TYPE)
    .map(media => listingPhotoUrl(media.url));
